import { Fragment, useState } from "react";
import API from "../../services/api.ts";
import { toast } from "react-toastify";

// Mirrors AnnotationsSummaryTab / ModelRunsSummaryTab so admins find the same
// shape in all three places: top stats row, per-study accordion, expandable
// detail row, CSV export. The detail row dumps the {component_id: value}
// answers map as a simple key/value table — we don't pull the form's field
// labels client-side (they live on the viewer config) since the CSV export
// already does that lookup server-side. Component ids are usually descriptive
// enough on their own (e.g. `initial_diagnosis`).
const ReviewerAnswersSummaryTab = ({
    submissions,
    summary,
    studies,
    loading,
    filters,
    onFilterChange,
    projectId,
    onRefresh,
}) => {
    const [expandedStudies, setExpandedStudies] = useState({});
    const [expandedSubs, setExpandedSubs] = useState({});
    const [exporting, setExporting] = useState(false);
    const [deletingSub, setDeletingSub] = useState(null);

    const handleDeleteSubmission = async (sub) => {
        const submitLabel = sub.submit_id || "whole form";
        const label = `${sub.patient_id || "(no patient)"} — ${submitLabel}`;
        if (!window.confirm(`Delete reviewer answer for ${label}? This cannot be undone.`)) return;
        try {
            setDeletingSub(sub.id);
            await API.delete(`/projects/${projectId}/reviewer-answers/${sub.id}/`);
            toast.success("Reviewer answer deleted.");
            if (onRefresh) onRefresh();
        } catch (err) {
            console.error("Error deleting reviewer answer:", err);
            toast.error(err.response?.data?.error || "Failed to delete reviewer answer.");
        } finally {
            setDeletingSub(null);
        }
    };

    const groupedByStudy = submissions.reduce((acc, s) => {
        const sid = s.study_id ?? "unassigned";
        if (!acc[sid]) acc[sid] = { studyName: s.study_name || "(no study)", subs: [] };
        acc[sid].subs.push(s);
        return acc;
    }, {});

    // Per-section time: gap between this submit and the prior one in the same
    // case (case scope = patient + reviewer + viewer + study). Backend's
    // `time_spent_seconds` is cumulative from case open, which makes follow-up
    // submits look long even when the reviewer only spent a few seconds on
    // that section. We keep the cumulative value as a tooltip.
    const sectionSecondsById = (() => {
        const groups = new Map();
        for (const s of submissions) {
            const caseKey = [
                s.study_id ?? "u",
                s.viewer_id ?? s.viewer_name ?? "",
                s.patient_id ?? "",
                s.user_id ?? s.username ?? "",
            ].join("|");
            if (!groups.has(caseKey)) groups.set(caseKey, []);
            groups.get(caseKey).push(s);
        }
        const out = new Map();
        for (const subs of groups.values()) {
            const ordered = [...subs].sort((a, b) => {
                const ta = a.submitted_at ? Date.parse(a.submitted_at) : 0;
                const tb = b.submitted_at ? Date.parse(b.submitted_at) : 0;
                return ta - tb;
            });
            let prevMs = null;
            for (const s of ordered) {
                if (prevMs === null) {
                    const startMs = s.started_at ? Date.parse(s.started_at) : null;
                    const submittedMs = s.submitted_at ? Date.parse(s.submitted_at) : null;
                    if (startMs && submittedMs) {
                        out.set(s.id, Math.max(0, Math.round((submittedMs - startMs) / 1000)));
                    } else {
                        out.set(s.id, null);
                    }
                } else {
                    const submittedMs = s.submitted_at ? Date.parse(s.submitted_at) : null;
                    if (submittedMs) {
                        out.set(s.id, Math.max(0, Math.round((submittedMs - prevMs) / 1000)));
                    } else {
                        out.set(s.id, null);
                    }
                }
                if (s.submitted_at) prevMs = Date.parse(s.submitted_at);
            }
        }
        return out;
    })();

    const toggleStudy = (sid) => setExpandedStudies((p) => ({ ...p, [sid]: !p[sid] }));
    const toggleSub = (id) => setExpandedSubs((p) => ({ ...p, [id]: !p[id] }));

    const handleExport = async () => {
        try {
            setExporting(true);
            const params = new URLSearchParams();
            if (filters.studyId) params.append("study_id", filters.studyId);
            const response = await API.get(
                `/projects/${projectId}/reviewer-answers/export/?${params.toString()}`,
                { responseType: "blob" }
            );
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
                "download",
                `reviewer_answers_${new Date().toISOString().split("T")[0]}.csv`
            );
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Reviewer answers exported successfully!");
        } catch (err) {
            console.error("Error exporting reviewer answers:", err);
            toast.error("Failed to export reviewer answers.");
        } finally {
            setExporting(false);
        }
    };

    const renderValue = (v) => {
        if (v === null || v === undefined || v === "") return <span className="text-muted">—</span>;
        if (typeof v === "boolean") return <span>{v ? "Yes" : "No"}</span>;
        if (typeof v === "number") return <span>{v}</span>;
        if (typeof v === "string") return <span style={{ whiteSpace: "pre-wrap" }}>{v}</span>;
        return <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{JSON.stringify(v, null, 2)}</pre>;
    };

    // Human-readable elapsed time. Mirrors PatientAnnotationSerializer.
    // get_time_spent_display so admins see the same format in both tabs.
    const formatTimeSpent = (seconds) => {
        const s = Number(seconds);
        if (!Number.isFinite(s) || s <= 0) return "—";
        if (s < 60) return `${s}s`;
        if (s < 3600) {
            const m = Math.floor(s / 60);
            const r = s % 60;
            return r ? `${m}m ${r}s` : `${m}m`;
        }
        const h = Math.floor(s / 3600);
        const m = Math.floor((s % 3600) / 60);
        return m ? `${h}h ${m}m` : `${h}h`;
    };

    if (loading) {
        return (
            <div className="card">
                <div className="card-body text-center py-5">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-muted">Loading reviewer answers...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Reviewer Answers</h5>
                <button
                    className="btn btn-success btn-sm"
                    onClick={handleExport}
                    disabled={submissions.length === 0 || exporting}
                >
                    {exporting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-1"></span>
                            Exporting...
                        </>
                    ) : (
                        <>
                            <i className="fa fa-download me-1"></i>
                            Export CSV
                        </>
                    )}
                </button>
            </div>

            <div className="card-body">
                {/* Summary */}
                {summary && (
                    <div className="row mb-4">
                        <div className="col-md-3">
                            <div className="bg-light p-3 rounded text-center">
                                <h4 className="mb-0">{summary.total_submissions}</h4>
                                <small className="text-muted">Total Submissions</small>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="bg-light p-3 rounded text-center">
                                <h4 className="mb-0">{summary.unique_patients}</h4>
                                <small className="text-muted">Unique Patients</small>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="bg-light p-3 rounded text-center">
                                <h4 className="mb-0">{summary.unique_users}</h4>
                                <small className="text-muted">Reviewers</small>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="bg-light p-3 rounded text-center">
                                <h4 className="mb-0">{summary.studies_with_submissions}</h4>
                                <small className="text-muted">Studies with Answers</small>
                            </div>
                        </div>
                    </div>
                )}

                {/* Filter */}
                <div className="row mb-3">
                    <div className="col-md-4">
                        <label className="form-label small text-muted">Filter by Study</label>
                        <select
                            className="form-select"
                            value={filters.studyId}
                            onChange={(e) => onFilterChange({ ...filters, studyId: e.target.value })}
                        >
                            <option value="">All Studies</option>
                            {studies.map((s) => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {submissions.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                        <i className="fa fa-clipboard mb-3" style={{ fontSize: "2.5rem", opacity: 0.5 }}></i>
                        <p>No reviewer answers yet.</p>
                        <small>Submissions appear here when reviewers click a Submit button in a scrollable viewer.</small>
                    </div>
                ) : (
                    <div className="accordion" id="reviewerAnswersAccordion">
                        {Object.entries(groupedByStudy).map(([sid, data]) => (
                            <div className="accordion-item" key={sid}>
                                <h2 className="accordion-header">
                                    <button
                                        className={`accordion-button ${expandedStudies[sid] ? "" : "collapsed"}`}
                                        type="button"
                                        onClick={() => toggleStudy(sid)}
                                    >
                                        <div className="d-flex align-items-center gap-2">
                                            <i className={`fa fa-chevron-${expandedStudies[sid] ? "down" : "right"}`}></i>
                                            <span className="fw-bold">{data.studyName}</span>
                                            <span className="badge bg-secondary">
                                                {data.subs.length} submission(s)
                                            </span>
                                        </div>
                                    </button>
                                </h2>
                                <div className={`accordion-collapse collapse ${expandedStudies[sid] ? "show" : ""}`}>
                                    <div className="accordion-body p-0">
                                        <div className="table-responsive">
                                            <table className="table table-sm table-hover mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>Patient ID</th>
                                                        <th>Reviewer</th>
                                                        <th>Viewer</th>
                                                        <th>Submit</th>
                                                        <th>Fields</th>
                                                        <th>Submitted</th>
                                                        <th>Time spent</th>
                                                        <th>Answers</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {data.subs.map((s) => {
                                                        const open = !!expandedSubs[s.id];
                                                        const answerEntries = Object.entries(s.answers || {});
                                                        return (
                                                            <Fragment key={s.id}>
                                                                <tr>
                                                                    <td><code>{s.patient_id}</code></td>
                                                                    <td>{s.username || <span className="text-muted">-</span>}</td>
                                                                    <td>{s.viewer_name || <span className="text-muted">-</span>}</td>
                                                                    <td>
                                                                        {s.submit_id ? (
                                                                            <code>{s.submit_id}</code>
                                                                        ) : (
                                                                            <span className="text-muted">whole form</span>
                                                                        )}
                                                                    </td>
                                                                    <td>
                                                                        <span className="badge bg-info text-dark">
                                                                            {answerEntries.length}
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        <small className="text-muted">
                                                                            {s.submitted_at ? new Date(s.submitted_at).toLocaleString() : "-"}
                                                                        </small>
                                                                    </td>
                                                                    <td>
                                                                        {(() => {
                                                                            const sectionSecs = sectionSecondsById.get(s.id);
                                                                            const cumulative = formatTimeSpent(s.time_spent_seconds);
                                                                            const titleParts = [];
                                                                            if (s.started_at) {
                                                                                titleParts.push(`Case opened ${new Date(s.started_at).toLocaleString()}`);
                                                                            }
                                                                            if (cumulative !== "—") {
                                                                                titleParts.push(`Cumulative since case open: ${cumulative}`);
                                                                            }
                                                                            return (
                                                                                <span title={titleParts.join("\n") || "No case-open timestamp recorded"}>
                                                                                    {formatTimeSpent(sectionSecs)}
                                                                                </span>
                                                                            );
                                                                        })()}
                                                                    </td>
                                                                    <td>
                                                                        <div className="d-flex gap-1">
                                                                            <button
                                                                                className="btn btn-outline-secondary btn-sm"
                                                                                onClick={() => toggleSub(s.id)}
                                                                                disabled={answerEntries.length === 0}
                                                                            >
                                                                                <i className={`fa fa-chevron-${open ? "up" : "down"} me-1`}></i>
                                                                                {open ? "Hide" : "View"}
                                                                            </button>
                                                                            <button
                                                                                className="btn btn-outline-danger btn-sm"
                                                                                onClick={() => handleDeleteSubmission(s)}
                                                                                disabled={deletingSub === s.id}
                                                                                title="Delete reviewer answer"
                                                                            >
                                                                                {deletingSub === s.id ? (
                                                                                    <span className="spinner-border spinner-border-sm"></span>
                                                                                ) : (
                                                                                    <i className="fa fa-trash"></i>
                                                                                )}
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                                {open && answerEntries.length > 0 && (
                                                                    <tr>
                                                                        <td colSpan={8} style={{ background: "#f7f9fc" }}>
                                                                            <table className="table table-sm mb-0" style={{ background: "transparent" }}>
                                                                                <tbody>
                                                                                    {answerEntries.map(([k, v]) => {
                                                                                        const label = (s.field_labels && s.field_labels[k]) || null;
                                                                                        return (
                                                                                            <tr key={k}>
                                                                                                <td style={{ width: 260 }}>
                                                                                                    {label && label !== k ? (
                                                                                                        <>
                                                                                                            <div style={{ fontWeight: 600, color: "#1f2937" }}>{label}</div>
                                                                                                            <code style={{ fontSize: 11, color: "#94a3b8" }}>{k}</code>
                                                                                                        </>
                                                                                                    ) : (
                                                                                                        <code style={{ color: "#5b6478" }}>{k}</code>
                                                                                                    )}
                                                                                                </td>
                                                                                                <td>{renderValue(v)}</td>
                                                                                            </tr>
                                                                                        );
                                                                                    })}
                                                                                </tbody>
                                                                            </table>
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </Fragment>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReviewerAnswersSummaryTab;
